import { pdsTv } from '@nestle-npus-nbm/pds-ui';
import AnalyticsPoint from 'gatsby-plugin-purina-analytics/AnalyticsPoint';
import { merge } from 'lodash';
import React from 'react';
import { Container } from 'react-bootstrap';
import { CmsButtonLink } from 'src/components/button/CmsButton';
import { CMS_THEME } from '../../common/enums';
import Highlight from '../../components/highlight/Highlight';
import RichText from '../../components/rich_text/RichText';
import { getMedia } from '../../utils/mediaHelpers';
import { StorageContentBlock } from '../common/StorageContentBlock/StorageContentBlock';

type Props = {
  node: IStorageHighlights;
};

const DefaultSettings = {
  entity_heading: {
    element: 'h2',
    style: 'typography_h2',
  },
  entity_buttons: {
    primary_button: 'btn-link',
  },
  entity_rows_columns: {
    columns: 3,
  },
  entity_variant: {
    theme: CMS_THEME.LIGHT,
    text_align: 'center',
  },
};

const baseContainer = pdsTv({
  base: 'pds-m-auto pds-grid pds-max-w-site-container pds-grid-cols-[minmax(0,360px)] pds-justify-center pds-gap-5 md:pds-grid-cols-3',
  variants: {
    cols: {
      3: 'lg:pds-grid-cols-3',
      4: 'lg:pds-grid-cols-4',
      5: 'lg:pds-grid-cols-5',
    },
  },
});

const styles = pdsTv({
  slots: {
    highlightContainer: 'pds-py-4 lg:pds-py-5.5',
  },
  variants: {
    theme: {
      light: { highlightContainer: 'pds-bg-surface pds-text-surface-text' },
      dark: { highlightContainer: 'pds-bg-surface-text pds-text-surface' },
      neutral: { highlightContainer: 'pds-bg-surface-layer pds-text-surface-text' },
    },
  },
});

const StorageHighlights: React.FC<Props> = function ({ node: highlights }: Props) {
  const settings = merge({}, DefaultSettings, highlights?.behaviors);
  const cmsTheme = settings.entity_variant.theme;
  const { element: HeadingElement } = settings.entity_heading;
  const primaryButtonVariation = settings.entity_buttons.primary_button;
  // @ts-ignore Always a heading element.
  const HighlightHeading: keyof JSX.IntrinsicElements =
    parseInt(HeadingElement.replace(/\D/g, ''), 10) < 5
      ? `h${parseInt(HeadingElement.replace(/\D/g, ''), 10) + 1}`
      : 'div';

  // If all highlights are draft, don't render.
  if (!highlights?.relationships?.highlights || highlights.relationships.highlights.length === 0) {
    return null;
  }

  const { highlightContainer } = styles({
    theme: cmsTheme,
  });

  const containerStyles = baseContainer({ cols: settings.entity_rows_columns.columns });

  return (
    <Container
      id={highlights.component_id}
      data-id={highlights.drupal_id}
      className={highlightContainer()}
      fluid
    >
      {(highlights?.title || highlights?.body?.processed) && (
        <Container>
          <StorageContentBlock
            node={{ ...highlights, behaviors: settings }}
            className="pds-mb-4.5 md:pds-mb-5.5"
          />
        </Container>
      )}

      <div className={containerStyles}>
        {highlights?.relationships?.highlights.map((highlight, index) => {
          const media = highlight.relationships?.media;
          return (
            <AnalyticsPoint type="component" node={highlight} key={index}>
              <Highlight
                drupalId={highlight.drupal_id}
                media={
                  media &&
                  getMedia({
                    media,
                    className: '!pds-rounded-circle',
                  })
                }
                actions={
                  highlight.link ? (
                    <CmsButtonLink
                      to={highlight.link.url}
                      {...highlight.link?.options?.attributes}
                      cmsTheme={cmsTheme}
                      variant={primaryButtonVariation}
                      className="pds-m-auto pds-w-fit"
                    >
                      {highlight.link.title}
                    </CmsButtonLink>
                  ) : undefined
                }
              >
                {highlight?.title && (
                  <HighlightHeading className="pds-mb-0 pds-text-title-md">
                    {highlight.title}
                  </HighlightHeading>
                )}
                {highlight?.body?.processed && (
                  <RichText
                    cmsTheme={cmsTheme}
                    className="[&>*]:pds-text-body-sm [&>*]:lg:pds-text-body-md"
                    body={highlight.body.processed}
                  />
                )}
              </Highlight>
            </AnalyticsPoint>
          );
        })}
      </div>
    </Container>
  );
};

export default StorageHighlights;
